import {
  computed,
} from '@vue/composition-api';

export default (props, context) => {
  const vehicleIcon = computed(() => {
    switch (context.attrs['product-name']) {
      case 'bike':
        return 'mdi-motorbike';
      case 'van':
        return 'mdi-van-utility';
      case 'motorhome':
        return 'mdi-rv-truck';
      case 'car':
      default:
        return 'mdi-car';
    }
  });

  /**
   * Generates a preview list from the data array based on the provided mappings (typically for Claims and Convictions data).
   *
   * The function processes each data object (from a subform or a larger dataset) and creates a formatted preview list.
   * Each field in the object is matched against the provided `mappings`, which control how the fields are presented in the
   * preview. Mappings include configuration for prepending or appending text and setting custom conditions for inclusion.
   *
   * Example `mappings` might be used to structure claims or conviction data for a vehicle insurance form, where each field
   * (such as claim type, date, or amount) is formatted according to the specific needs of the application.
   *
   * @param {Array<Object>} dataArray - Array of data objects to process. Each object could represent a form or subform entry
   * for a claim, conviction, or other type of record. Each object must have an `id` field and other fields that match those in `mappings`.
   * @param {string|number} itemId - The ID of the item to match in the `dataArray`. This is used to find a specific entry
   * in the data array that needs to be processed and previewed.
   *
   * @param {Object} mappings - Object where keys represent fields in the data object, and values define how each field
   * should be formatted in the preview list. Each mapping entry contains a `prepend`, `value`, and `append` configuration,
   * as well as an optional:
   *  `condition` function that determines if the field should be included in the preview.
   *  `error` boolean value that changes the text to red if true
   * @returns {Array<Object>} - The generated preview list containing formatted objects with the specified `prepend`, `value`,
   * `append` and `error` from the mappings. If no matching data is found for the given `itemId`, returns an empty array.
   */
  const generateClaimsConvictionsPreviewList = (dataArray, itemId, mappings) => {
    const data = dataArray.find(entry => entry.id === itemId);

    if (!data) {
      return [];
    }

    return Object.entries(mappings).reduce((acc, [field, config]) => {
      const expiredField = `${field}Expired`;
      const fieldExists = Object.prototype.hasOwnProperty.call(data, field) || Object.prototype.hasOwnProperty.call(data, expiredField);
      const isFieldValid = ![null, undefined, ''].includes(data[field]) || ![null, undefined, ''].includes(data[expiredField]);
      const condition = !config.condition || config.condition();

      if (fieldExists && isFieldValid && condition) {
        acc.push({
          prepend: config.prepend,
          value: config.value,
          append: config.append,
          error: config.error,
        });
      }

      return acc;
    }, []);
  };

  return {
    // methods
    vehicleIcon,
    generateClaimsConvictionsPreviewList,
  };
};
